import { useEffect, useRef, type ReactNode } from 'react';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
  /** Small centred dialog instead of a bottom sheet / side panel. */
  compact?: boolean;
}

/**
 * Bottom sheet on native <dialog>: focus trap, Esc and backdrop close.
 * On iOS the keyboard does not shrink 100dvh, so the visible viewport height is tracked in --vvh.
 */
export function Sheet({ open, onClose, label, children, compact = false }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!open || !vv) return;
    const update = () => {
      document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
    };
    update();
    vv.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      document.documentElement.style.removeProperty('--vvh');
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={compact ? 'sheet sheet-compact' : 'sheet'}
      aria-label={label}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {open && (
        <div
          className={`pb-safe flex flex-col ${
            compact
              ? 'max-h-[80dvh]'
              : 'max-h-[calc(var(--vvh,100dvh)-1.5rem)] lg:h-dvh lg:max-h-dvh'
          }`}
        >
          {!compact && (
            <div
              className="sheet-grip mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-line"
              aria-hidden
            />
          )}
          {children}
        </div>
      )}
    </dialog>
  );
}
